import { motion } from 'motion/react';
import type { FC, PropsWithChildren } from 'react';
import React from 'react';

import { cn, useComponentTheme } from '@/utils';

import type {
  NavigationButtonVariantTheme,
  NavigationTheme
} from './NavigationTheme';

interface NavigationButtonProps extends PropsWithChildren {
  /**
   * Custom class names for the navigation button.
   */
  className?: string;

  /**
   * Variant of the navigation button.
   * @default 'ghost'
   */
  variant?: keyof NavigationButtonVariantTheme;

  /**
   * Indicates if the button is active.
   */
  active?: boolean;

  /**
   * Custom theme for the navigation button.
   */
  theme?: NavigationTheme;

  /**
   * Disables the button, preventing interaction.
   */
  disabled?: boolean;

  /**
   * If false, the animation of the button will be disabled.
   * @default true
   */
  animated?: boolean;

  /**
   * Unique identifier for the animation layout.
   */
  animationLayoutId?: string;

  /**
   * Callback function to handle click events on the button.
   */
  onClick?: () => void;
}

export const NavigationButton: FC<NavigationButtonProps> = ({
  className,
  active,
  children,
  theme,
  disabled,
  animated = true,
  variant = 'ghost',
  animationLayoutId,
  onClick
}) => {
  const navigationTheme: NavigationTheme = useComponentTheme(
    'navigation',
    theme
  );

  return (
    <div className={navigationTheme.button.base}>
      {active && (
        <motion.div
          layoutId={
            !animated ? undefined : (animationLayoutId ?? 'selected-nav-button')
          }
          className={navigationTheme.button.variant?.[variant]?.selection}
        />
      )}
      <motion.button
        disabled={disabled}
        whileTap={{ scale: disabled || !animated || active ? 1 : 0.9 }}
        className={cn(
          navigationTheme.button?.variant?.[variant]?.content,
          { [navigationTheme.button?.variant?.[variant]?.active]: active },
          { [navigationTheme.button?.variant?.[variant]?.disabled]: disabled },
          className
        )}
        onClick={onClick}
      >
        {children}
      </motion.button>
    </div>
  );
};
